import { toast } from 'sonner';
import { z } from 'zod';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ProfilePhotoInput } from '@/components/ui/profile-photo-input';
import { DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useEditProfileForm } from '../useAuthForm';
import { editProfileFormSchema } from '../auth.schema';
import { useUpdateUserDataMutation } from '../auth.api.slice';
import PreferenceToggleField from '@/components/common/PreferenceToggleField';
import { SOCIAL_PLATFORMS } from '@/pages/Profile/socialPlatforms';

interface ProfileFormProps {
  defaultValues?: z.infer<typeof editProfileFormSchema>;
  handleDialog: (open: boolean) => void;
}

/**
 * Build the update payload, layering defaults under form values so that
 * privacy / socialLinks / notificationPreferences are always sent even
 * when their tabs haven't been opened (Radix unmounts inactive TabsContent
 * children, so RHF never registers those fields).
 */
function buildUpdatePayload(
  values: z.infer<typeof editProfileFormSchema>,
  defaults?: z.infer<typeof editProfileFormSchema>
) {
  const { privacy, socialLinks, notificationPreferences } = defaults ?? {};
  const { profilePicture, ...rest } = values;
  return {
    privacy,
    socialLinks,
    notificationPreferences,
    ...rest,
    ...(profilePicture instanceof File && { profilePicture }),
  };
}

function EditProfileForm({ defaultValues, handleDialog }: ProfileFormProps) {
  const form = useEditProfileForm(defaultValues);
  const [updateUser, { isLoading: isUpdateUserLoading }] =
    useUpdateUserDataMutation();

  // Open the links accordion by default only when the user already has some.
  const hasLinks = Boolean(
    defaultValues?.socialLinks &&
      Object.values(defaultValues.socialLinks).some(Boolean)
  );

  const handleUpdateUser = async (
    values: z.infer<typeof editProfileFormSchema>
  ) => {
    try {
      await updateUser(buildUpdatePayload(values, defaultValues)).unwrap();
      toast.success('Profile updated successfully');
      handleDialog(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(handleUpdateUser)}
      >
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="privacy">Privacy</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 pt-2">
            <div className="flex flex-col items-center gap-2 py-2">
              <FormField
                control={form.control}
                name="profilePicture"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center gap-2">
                    <FormControl>
                      <ProfilePhotoInput
                        {...field}
                        value={field.value}
                        onChange={field.onChange}
                        className="h-24 w-24"
                      />
                    </FormControl>
                    <FormLabel>Profile Photo</FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Accordion
              type="single"
              collapsible
              defaultValue={hasLinks ? 'links' : undefined}
            >
              <AccordionItem value="links" className="rounded-lg border px-4">
                <AccordionTrigger className="py-3 text-sm">
                  Links (Optional)
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform" />
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {SOCIAL_PLATFORMS.map((platform) => (
                    <FormField
                      key={platform.key}
                      control={form.control}
                      name={`socialLinks.${platform.key}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <platform.icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              <Input
                                className="pl-9"
                                placeholder={`${platform.label} — ${platform.placeholder}`}
                                {...field}
                                value={field.value ?? ''}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>

          <TabsContent value="privacy" className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Control which information is visible on your public profile
            </p>

            <PreferenceToggleField
              control={form.control}
              name="privacy.email"
              label="Email"
              description="Show your email address on your profile"
            />
            <PreferenceToggleField
              control={form.control}
              name="privacy.firstname"
              label="First Name"
              description="Show your first name on your profile"
            />
            <PreferenceToggleField
              control={form.control}
              name="privacy.lastname"
              label="Last Name"
              description="Show your last name on your profile"
            />
            <PreferenceToggleField
              control={form.control}
              name="privacy.links"
              label="Links"
              description="Show your links on your profile"
            />
          </TabsContent>

          <TabsContent value="notifications" className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Choose which emails you&apos;d like to receive
            </p>

            <PreferenceToggleField
              control={form.control}
              name="notificationPreferences.coinsEarned"
              label="Coins earned"
              description="Email me when I earn coins"
            />
            <PreferenceToggleField
              control={form.control}
              name="notificationPreferences.newPosts"
              label="New posts from creators you follow"
              description="Email me when someone I follow publishes a project"
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={isUpdateUserLoading}>
            Save changes
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export default EditProfileForm;
